<template>
  <div class="pivot-schedule">
    <b-button variant="3v3" block size="sm" class="mb-2" @click="openForm(null)">
      + Nova agenda
    </b-button>

    <div v-if="todayTimeline.length" class="pivot-timeline">
      <b-form-text>Hoje</b-form-text>
      <div class="pivot-timeline-bar">
        <div v-for="(seg, i) in todayTimeline" :key="i" :class="['pivot-timeline-seg', `seg-${seg.color}`]"
          :style="{ flexBasis: seg.percent + '%' }" :title="`${seg.name} (${seg.startLabel} - ${seg.endLabel})`">
        </div>
      </div>
      <div class="pivot-timeline-labels">
        <span>00:00</span>
        <span>12:00</span>
        <span>24:00</span>
      </div>
    </div>

    <div v-if="loading" class="obs-info">Carregando...</div>
    <div v-else-if="!schedules.length" class="obs-info">
      <em>Nenhuma agenda cadastrada pra este pivô.</em>
    </div>
    <div v-else class="pivot-schedule-list">
      <div v-for="s in schedules" :key="s.ID" :class="['pivot-schedule-card', !isActive(s) ? 'inactive' : '']"
        @click="openForm(s)">
        <div class="pivot-schedule-head">
          <strong>{{ s.NAME }}</strong>
          <div class="pivot-schedule-head-right">
            <span class="pivot-schedule-window">
              {{ (s.START_TIME || "").slice(0, 5) }} - {{ scheduleEndLabel(s) }}
            </span>
            <b-form-checkbox switch size="sm" class="pivot-schedule-active-toggle" :checked="isActive(s)"
              @click.native.prevent.stop="askToggleActive(s)" :title="isActive(s) ? 'Habilitada' : 'Desabilitada'">
            </b-form-checkbox>
          </div>
        </div>
        <div v-if="s.RECURRENCE_MODE === 'WEEKDAYS'" class="pivot-schedule-days">
          <span v-for="(d, i) in days" :key="i" :class="['day-dot', isDayActive(s.ACTIVITY_DAYS, i) ? 'active' : '']">
            {{ d.text }}
          </span>
        </div>
        <div v-else class="pivot-schedule-days">
          <span class="tag">{{ s.RECURRENCE_MODE === 'ODD' ? 'Dias ímpares' : 'Dias pares' }}</span>
        </div>
        <div class="pivot-schedule-tags">
          <span v-if="s.REVERSED !== null" class="tag">{{ s.REVERSED ? "Reversão" : "Avanço" }}</span>
          <span v-if="s.WET_MODE !== null" class="tag">{{ s.WET_MODE ? "Molhado" : "Seco" }}</span>
          <span v-if="s.CANNON !== null" class="tag">Canhão {{ s.CANNON ? "ligado" : "desligado" }}</span>
          <span v-if="s.BLADE_MM" class="tag">{{ s.BLADE_MM }} mm</span>
          <span v-if="s.SPEED_PERCENT" class="tag">{{ s.SPEED_PERCENT }}%</span>
          <span v-if="s.FOLLOW_ANGLE && s.END_ANGLE !== null" class="tag">
            Parar em {{ s.END_ANGLE }}°
          </span>
          <span v-else-if="s.FOLLOW_ANGLE" class="tag tag-warning"
            title="Sem ângulo final o pivô não para na posição final. Edite a agenda para informar o ângulo.">
            Posição final sem ângulo
          </span>
          <span v-if="s.PEAK_HOUR_BLOCK" class="tag tag-warning">Pausa no hor. de ponta</span>
          <span v-if="s.SENSOR_NAME" class="tag tag-warning">Gatilho: {{ s.SENSOR_NAME }}</span>
          <span v-if="hasConflict(s)" class="tag tag-danger" :title="conflictTitle(s)">
            ⚠ Conflito de horário
          </span>
        </div>
        <div v-if="liveStatus(s)" class="pivot-schedule-live">
          <span v-if="scheduleOverride(s)" :class="['live-status-badge', `live-status-${scheduleOverride(s).kind}`]">
            {{ scheduleOverride(s).label }}
          </span>
          <span v-else :class="['live-status-badge', `live-status-${liveStatus(s).STATUS}`]">
            {{ statusLabel(liveStatus(s).STATUS) }}
          </span>
          <div v-if="scheduleOverride(s)" :class="['live-reason', `live-${scheduleOverride(s).kind}`]">
            {{ scheduleOverride(s).reason }}
          </div>
          <div v-else-if="liveStatus(s).STATUS === 9" class="live-progress">
            <div class="live-progress-bar" :style="{ width: liveProgressPercent(s) + '%' }"></div>
            <span class="live-progress-text">{{ liveStatus(s).ELAPSED }}/{{ s.DURATION_MIN }} min</span>
          </div>
          <div v-if="liveReason(s)" :class="['live-reason', { 'live-reason-info': isNormalStatus(liveStatus(s).STATUS) }]">
            {{ liveReason(s) }}
          </div>
          <div v-else-if="needsReason(liveStatus(s).STATUS)" class="live-reason live-reason-missing">
            (motivo não informado pelo firmware)
          </div>
          <div v-if="!scheduleOverride(s) && angleReached(s)" class="live-reason live-reason-info">
            Ocorrência encerrada ao chegar na posição final. O pivô fica parado até o fim da janela.
          </div>
          <div v-if="!scheduleOverride(s) && positionUnavailable(s)" class="live-reason live-position-unavailable">
            ⚠ Posição indisponível: o pivô não vai parar na posição final e segue pela duração da agenda.
          </div>
          <template v-if="isLiveWindowStatus(liveStatus(s).STATUS)">
            <div class="live-equipment">Equipamento agora: {{ equipmentSummary() }}</div>
            <div v-if="liveMismatch(s)" class="live-reason live-mismatch">
              ⚠ O equipamento parece ativo, mas a agenda diz "{{ statusLabel(liveStatus(s).STATUS) }}" -- pode ser um status desatualizado do firmware.
            </div>
          </template>
        </div>
      </div>
    </div>

    <b-modal v-model="showForm" ok-title="Salvar" ok-variant="3v3" cancel-title="Cancelar"
      cancel-variant="outline-secondary" :ok-disabled="!valid" @ok="save" centered scrollable>
      <template #modal-title>
        {{ form.ID ? "Editar agenda" : "Nova agenda" }}
        <span v-if="form.ID" :class="['tag', 'schedule-status-tag', isActive(form) ? '' : 'tag-danger']">
          {{ isActive(form) ? "Habilitada" : "Desabilitada" }}
        </span>
      </template>
      <b-form-text>Nome *</b-form-text>
      <b-form-input v-model="form.NAME" size="sm" placeholder="Nome da agenda" />

      <div class="row mt-2">
        <div class="col-xs-6">
          <b-form-text>Início *</b-form-text>
          <b-form-input v-model="form.START_DATE" type="date" size="sm" />
          <b-form-input v-model="form.START_TIME" type="time" size="sm" class="mt-1" />
        </div>
        <div class="col-xs-6">
          <b-form-text>Duração (min) *</b-form-text>
          <b-form-input v-model.number="form.DURATION_MIN" type="number" min="1" max="1440" step="1" size="sm"
            :disabled="allDayDuration" :state="durationValid(form.DURATION_MIN) ? null : false" />
          <b-form-invalid-feedback v-if="!durationValid(form.DURATION_MIN)">
            Entre 1 e 1440 minutos (24h).
          </b-form-invalid-feedback>
          <b-form-checkbox size="sm" v-model="allDayDuration" switch class="mt-1">
            Dia todo
          </b-form-checkbox>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col-xs-6">
          <b-form-checkbox size="sm" v-model="hasEnd" switch class="mt-1">
            Definir expiração?
          </b-form-checkbox>
          <template v-if="hasEnd">
            <b-form-input v-model="form.END_DATE" type="date" size="sm" class="mt-1" />
            <b-form-input v-model="form.END_TIME" type="time" size="sm" class="mt-1" />
          </template>
        </div>
      </div>

      <b-form-text class="mt-2">Recorrência</b-form-text>
      <b-form-radio-group v-model="form.RECURRENCE_MODE" :options="recurrenceOptions" size="sm" buttons
        button-variant="outline-secondary" class="w-100 mb-2" />

      <center v-if="form.RECURRENCE_MODE === 'WEEKDAYS'" class="mt-2">
        <b-form-text>Dias de atuação</b-form-text>
        <b-button-group>
          <b-btn v-for="(d, index) in days" :key="index" @click="toggleDay(index)"
            :variant="isDayActive(form.ACTIVITY_DAYS, index) ? '3v3' : 'danger'" size="sm">
            {{ d.text }}
          </b-btn>
        </b-button-group>
      </center>
      <div v-else class="obs-info mb-2">
        <em>{{ form.RECURRENCE_MODE === 'ODD' ? 'Atua nos dias ímpares do mês (1, 3, 5...).' : 'Atua nos dias pares do mês (2, 4, 6...).' }}</em>
      </div>

      <div class="info-title mt-3">Agenda de operação</div>
      <b-form-text>
        Sentido <span v-if="currentDirection" class="current-value">(atual: {{ currentDirection }})</span>
      </b-form-text>
      <b-form-radio-group v-model="form.REVERSED" :options="sentidoOptions" size="sm" buttons
        button-variant="outline-secondary" class="w-100 mb-2" />

      <b-form-text>
        Modo <span v-if="currentMode" class="current-value">(atual: {{ currentMode }})</span>
      </b-form-text>
      <b-form-radio-group v-model="form.WET_MODE" :options="modoOptions" size="sm" buttons
        button-variant="outline-secondary" class="w-100 mb-2" />

      <b-form-text>
        Canhão final <span v-if="currentCannon" class="current-value">(atual: {{ currentCannon }})</span>
      </b-form-text>
      <b-form-radio-group v-model="form.CANNON" :options="canhaoOptions" size="sm" buttons
        button-variant="outline-secondary" class="w-100 mb-2" />

      <template v-if="hasPositionSensor">
        <b-form-checkbox size="sm" v-model="form.FOLLOW_ANGLE" switch class="mt-2">
          Parar na posição final
        </b-form-checkbox>
        <template v-if="form.FOLLOW_ANGLE">
          <b-form-text>
            Ângulo final <span v-if="currentAngle !== null" class="current-value">(atual: {{ currentAngle.toFixed(0) }}°)</span>
          </b-form-text>
          <b-form-input v-model.number="form.END_ANGLE" type="number" min="0" max="360" step="1" size="sm"
            placeholder="Até (°)" :state="angleValid(form.END_ANGLE) && !missingEndAngle ? null : false" />
          <div v-if="missingEndAngle" class="obs-info mb-2 text-danger">
            <em>Informe o ângulo final ou desligue "Parar na posição final".</em>
          </div>
          <div v-else-if="!angleValid(form.END_ANGLE)" class="obs-info mb-2 text-danger">
            <em>Ângulo deve estar entre 0 e 360.</em>
          </div>
        </template>
      </template>
      <div v-else class="obs-info mb-2">
        <em>Agendamento por ângulo requer um sensor de posição (SPG) associado a este pivô.</em>
      </div>

      <div class="row">
        <div class="col-xs-6">
          <b-form-text>Lâmina desejada (mm)</b-form-text>
          <b-form-input v-model.number="form.BLADE_MM" type="number" min="0" step="0.1" size="sm" />
        </div>
        <div class="col-xs-6">
          <b-form-text>
            Percentímetro (%) <span v-if="currentPercent !== null" class="current-value">(atual: {{ currentPercent }}%)</span>
          </b-form-text>
          <b-form-input v-model.number="form.SPEED_PERCENT" type="number" min="0" max="100" step="1" size="sm" />
        </div>
      </div>

      <b-form-checkbox size="sm" v-model="form.PEAK_HOUR_BLOCK" switch class="mt-3">
        Pausar no horário de ponta
      </b-form-checkbox>
      <div v-if="form.PEAK_HOUR_BLOCK" class="obs-info mb-2">
        <em>Janela configurada em Configurações → Geral.</em>
      </div>

      <b-form-checkbox size="sm" v-model="conditional" switch class="mt-3">
        Definir condição por sensor?
      </b-form-checkbox>
      <div v-if="conditional" class="row mt-1">
        <div class="col-xs-12">
          <b-form-text>Sensor (ex.: pluviômetro) *</b-form-text>
          <searchable-select v-model="form.FK_SENSOR" :options="sensors" text-field="NAME" value-field="ID"
            state="valid" placeholder="Sensor" />
        </div>
        <div class="col-xs-12">
          <b-form-text>Medição do sensor</b-form-text>
          <b-form-select :disabled="form.FK_SENSOR === null" v-model="form.POS_SENSOR" size="sm">
            <template slot="first">
              <option :value="null">Medição do sensor</option>
            </template>
            <option v-for="(measure, index) in auxSensors.filter((e) => e.ID == form.FK_SENSOR)" :key="index"
              :value="measure.POS">
              {{ measure.NAME_TYPE }} ({{ measure.NAME_UNIT }})
            </option>
          </b-form-select>
        </div>
        <div class="col-xs-4">
          <b-form-text>Ação</b-form-text>
          <b-form-select :disabled="form.POS_SENSOR === null" v-model="form.ACTION_SENSOR" size="sm">
            <option v-for="opt in [0, 1]" :key="opt" :value="opt">
              {{ opt ? "Direta" : "Inversa" }}
            </option>
          </b-form-select>
        </div>
        <div class="col-xs-4">
          <b-form-text>Mínimo</b-form-text>
          <b-form-input :disabled="form.POS_SENSOR === null" v-model.number="form.MIN_SENSOR" type="number"
            size="sm" />
        </div>
        <div class="col-xs-4">
          <b-form-text>Máximo</b-form-text>
          <b-form-input :disabled="form.POS_SENSOR === null" v-model.number="form.MAX_SENSOR" type="number"
            size="sm" />
        </div>
        <div class="col-xs-12 obs-info mt-1">
          <em>Ex.: pluviômetro, Ação "Direta", Mínimo 10 -- bloqueia se choveu mais de 10mm.</em>
        </div>
      </div>

      <b-button v-if="form.ID" variant="outline-danger" block size="sm" class="mt-4"
        @click="showForm = false; showDeleteConfirm = true">
        Remover agenda
      </b-button>
    </b-modal>

    <b-modal v-model="showDeleteConfirm" title="Remover agenda" ok-title="Remover" ok-variant="danger"
      cancel-title="Cancelar" cancel-variant="outline-secondary" @ok="remove" centered>
      Remover a agenda <strong>{{ form.NAME }}</strong>?
    </b-modal>

    <b-modal v-model="showActiveConfirm" :title="activeTarget && isActive(activeTarget) ? 'Desabilitar agenda' : 'Habilitar agenda'"
      :ok-title="activeTarget && isActive(activeTarget) ? 'Desabilitar' : 'Habilitar'" ok-variant="3v3"
      cancel-title="Cancelar" cancel-variant="outline-secondary" @ok="confirmToggleActive" @cancel="activeTarget = null" centered>
      <span v-if="activeTarget">
        {{ isActive(activeTarget) ? "Desabilitar" : "Habilitar" }} a agenda <strong>{{ activeTarget.NAME }}</strong>?
        <span v-if="isActive(activeTarget)"> Ela deixa de ser aplicada até ser habilitada de novo.</span>
      </span>
    </b-modal>
  </div>
</template>

<script>
import { pirSchedules, valves, sensors } from "@/api";
import {
  pivotDirection,
  pivotWorkMode,
  pivotCannonStatus,
  pivotPercent,
  pivotAngle,
  pivotPositionFailed,
  pivotStatusText,
  PIR_SCHEDULE_STATUS_LABELS,
  PIR_SCHEDULE_LIVE_STATUSES,
  PIR_SCHEDULE_SAFETY_FAULT,
  PIR_SCHEDULE_ANGLE_REACHED,
  pirSchedulePosition,
  pirScheduleOverride,
} from "../pirHelpers";
import SearchableSelect from "@/pages/components/shared/SearchableSelect.vue";

const DAYS = [
  { text: "D", value: 1 },
  { text: "S", value: 2 },
  { text: "T", value: 4 },
  { text: "Q", value: 8 },
  { text: "Q", value: 16 },
  { text: "S", value: 32 },
  { text: "S", value: 64 },
];

function triBool(v) {
  return v === null || v === undefined ? null : !!Number(v);
}

const EMPTY_FORM = () => ({
  ID: null,
  NAME: "",
  FK_VALVE: null,
  START_DATE: "",
  START_TIME: "",
  DURATION_MIN: 60,
  END_DATE: null,
  END_TIME: null,
  ACTIVITY_DAYS: 127,
  RECURRENCE_MODE: "WEEKDAYS",
  REVERSED: null,
  WET_MODE: null,
  CANNON: null,
  BLADE_MM: null,
  SPEED_PERCENT: null,
  END_ANGLE: null,
  FOLLOW_ANGLE: false,
  PEAK_HOUR_BLOCK: false,
  FK_SENSOR: null,
  POS_SENSOR: null,
  MIN_SENSOR: null,
  MAX_SENSOR: null,
  ACTION_SENSOR: null,
});

export default {
  name: "pivot-schedule",
  components: {
    SearchableSelect,
  },
  props: {
    item: Object,
  },
  data() {
    return {
      days: DAYS,
      loading: false,
      schedules: [],
      pirMonitoring: {},
      monitorTimeout: null,
      hasPositionSensor: false,
      showForm: false,
      showDeleteConfirm: false,
      showActiveConfirm: false,
      activeTarget: null,
      hasEnd: false,
      allDayDuration: false,
      conditional: false,
      sensors: [],
      auxSensors: [],
      form: EMPTY_FORM(),
      sentidoOptions: [
        { text: "Não alterar", value: null },
        { text: "Avanço", value: false },
        { text: "Reversão", value: true },
      ],
      modoOptions: [
        { text: "Não alterar", value: null },
        { text: "Seco", value: false },
        { text: "Molhado", value: true },
      ],
      canhaoOptions: [
        { text: "Não alterar", value: null },
        { text: "Ligado", value: true },
        { text: "Desligado", value: false },
      ],
      recurrenceOptions: [
        { text: "Dias da semana", value: "WEEKDAYS" },
        { text: "Dias ímpares", value: "ODD" },
        { text: "Dias pares", value: "EVEN" },
      ],
    };
  },
  computed: {
    valid() {
      return !!(
        this.form.NAME &&
        this.form.START_DATE &&
        this.form.START_TIME &&
        this.durationValid(this.form.DURATION_MIN) &&
        this.angleValid(this.form.END_ANGLE) &&
        !this.missingEndAngle
      );
    },
    // Sem ângulo o fw_cir ignora FOLLOW_ANGLE e o pivô não para, mas a chave
    // "Parar na posição final" ligada dizia o contrário (16 agendas assim na
    // karitel). Só exige com a chave visível: sem sensor de posição ela some
    // e a agenda ficaria impossível de salvar.
    missingEndAngle() {
      const a = this.form.END_ANGLE;
      return this.hasPositionSensor && !!this.form.FOLLOW_ANGLE && (a === null || a === undefined || a === "");
    },
    // Estado ao vivo do PIR (item.GENERIC) exibido como referência clara ao lado
    // de cada campo -- só orientativo, não interfere no valor da agenda (que
    // continua null por padrão, ou seja, "não alterar" nessa janela).
    currentDirection() {
      return pivotDirection(this.item);
    },
    currentMode() {
      return pivotWorkMode(this.item);
    },
    currentCannon() {
      return pivotCannonStatus(this.item);
    },
    currentPercent() {
      return pivotPercent(this.item);
    },
    currentAngle() {
      if (pivotPositionFailed(this.item)) return null;
      return pivotAngle(this.item);
    },
    // Timeline de hoje: cada agenda ativa vira um segmento do horário dela até
    // horário + DURATION_MIN. A barra em si é de um dia só (0-100% = hoje
    // 00:00-24:00), então o segmento visual é clampado em 24:00 mesmo quando
    // a agenda continua amanhã -- mas o rótulo de fim usa scheduleEndLabel()
    // (com virada de dia) pra não mostrar "24:00" errado no texto. Buracos
    // entre segmentos = janela ociosa. Agendas que se sobrepõem (ver
    // scheduleConflicts) não ganham tratamento visual especial aqui -- o
    // sinal de sobreposição é a tag "Conflito de horário" no card, não a
    // timeline.
    todayTimeline() {
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      const active = this.schedules.filter((s) => {
        if (!this.isActive(s)) return false;
        if (s.START_DATE > todayStr) return false;
        if (s.END_DATE && s.END_DATE < todayStr) return false;
        if (s.RECURRENCE_MODE === "ODD") return now.getDate() % 2 === 1;
        if (s.RECURRENCE_MODE === "EVEN") return now.getDate() % 2 === 0;
        return this.isDayActive(s.ACTIVITY_DAYS, now.getDay());
      });
      const sorted = [...active].sort(
        (a, b) => this.toMinutesOfDay(a.START_TIME) - this.toMinutesOfDay(b.START_TIME)
      );
      if (!sorted.length) return [];

      const segments = [];
      let cursor = 0;
      for (const s of sorted) {
        const start = this.toMinutesOfDay(s.START_TIME);
        const visualEnd = Math.min(start + Number(s.DURATION_MIN || 0), 1440);
        if (visualEnd <= start) continue;
        if (start > cursor) {
          segments.push({
            name: "Ocioso",
            startLabel: this.minutesLabel(cursor),
            endLabel: this.minutesLabel(start),
            percent: ((start - cursor) / 1440) * 100,
            color: "idle",
          });
        }
        // WET_MODE vem 0/1/null do banco (tinyint), não true/false -- mesmo
        // cuidado do triBool() usado em openForm().
        const wet = triBool(s.WET_MODE);
        segments.push({
          name: s.NAME,
          startLabel: this.minutesLabel(start),
          endLabel: this.scheduleEndLabel(s),
          percent: ((visualEnd - start) / 1440) * 100,
          color: wet === true ? "wet" : wet === false ? "dry" : "neutral",
        });
        cursor = Math.max(cursor, visualEnd);
      }
      return segments;
    },
    // Mapa ID -> lista de agendas habilitadas do mesmo PIR que colidem (janela
    // [START_TIME, START_TIME+DURATION_MIN) sobreposta, em dias/validade que
    // se cruzam) -- só entre habilitadas, já que uma desabilitada não dispara
    // nada mesmo. Horários de início diferentes ainda podem colidir se a
    // duração de uma alcançar o início da outra.
    scheduleConflicts() {
      const map = {};
      const enabled = this.schedules.filter((s) => this.isActive(s));
      for (let i = 0; i < enabled.length; i++) {
        for (let j = i + 1; j < enabled.length; j++) {
          const a = enabled[i];
          const b = enabled[j];
          if (this.schedulesConflict(a, b)) {
            (map[a.ID] = map[a.ID] || []).push(b);
            (map[b.ID] = map[b.ID] || []).push(a);
          }
        }
      }
      return map;
    },
  },
  created() {
    this.loadSensors();
    this.pollMonitoring();
  },
  beforeDestroy() {
    this._destroyed = true;
    clearTimeout(this.monitorTimeout);
  },
  watch: {
    item: {
      immediate: true,
      handler(newItem, oldItem) {
        if (newItem && newItem.ID && (!oldItem || newItem.ID !== oldItem.ID)) {
          this.fetchList();
          this.checkPositionSensor();
        }
      },
    },
    allDayDuration(v) {
      if (v) {
        this.form.DURATION_MIN = 1440;
      } else if (this.form.DURATION_MIN === 1440) {
        this.form.DURATION_MIN = 60;
      }
    },
    "form.FOLLOW_ANGLE"(v) {
      if (!v) this.form.END_ANGLE = null;
    },
  },
  methods: {
    // Status calculado pelo executor real da agenda do PIR (fw_cir, issue
    // #198), lido de /mnt/fcir/agn_pir/<id>.txt via GET
    // /get-pir-schedule-monitoring. ID chega como string do JSON (mesmo
    // cuidado de sempre: Number() nos dois lados antes de comparar).
    pollMonitoring() {
      pirSchedules
        .monitoring()
        .then((ret) => {
          const map = {};
          (ret || []).forEach((m) => {
            map[Number(m.ID)] = m;
          });
          this.pirMonitoring = map;
        })
        .catch((err) => console.log(err))
        .then(() => {
          if (!this._destroyed) {
            this.monitorTimeout = setTimeout(this.pollMonitoring, 3000);
          }
        });
    },
    liveStatus(s) {
      return this.pirMonitoring[Number(s.ID)];
    },
    statusLabel(code) {
      return PIR_SCHEDULE_STATUS_LABELS[code] || "-";
    },
    scheduleOverride(s) {
      return pirScheduleOverride(this.liveStatus(s), this.item);
    },
    liveProgressPercent(s) {
      const m = this.liveStatus(s);
      if (!m || !s.DURATION_MIN) return 0;
      return Math.min(100, Math.round((Number(m.ELAPSED) / Number(s.DURATION_MIN)) * 100));
    },
    // GENERIC sempre inclui {NAME:"ID:", VALUE:...} (mesmo padrão do JSON do
    // AGN_TIME) -- irrelevante pra exibir aqui, só as mensagens de
    // bloqueio/conflito interessam.
    liveReason(s) {
      const m = this.liveStatus(s);
      if (!m || !m.GENERIC) return "";
      const relevant = m.GENERIC.filter((g) => g.NAME !== "ID:");
      if (!relevant.length) return "";
      return relevant.map((g) => `${g.NAME} ${g.VALUE}`).join(" · ");
    },
    // Estados que sempre vêm com um motivo em GENERIC (conflito/ponta/
    // sensor/equipamento indisponível -- ver agn_pir.c get_status_agn_pir()
    // + o bloco de fprintf "GENERIC" logo abaixo, que só popula o motivo
    // pra CONFLICT/PEAK_BLOCKED/SENSOR_LOCKED/UNAVAILABLE). RUNNING(9) fica
    // de fora de propósito: o motivo dela (ângulo) é opcional -- a maioria
    // das agendas rodando não tem FOLLOW_ANGLE, então não ter motivo aí é
    // normal, não falha do firmware (era isso que gerava o fallback confuso
    // "(motivo não informado)" numa agenda rodando perfeitamente).
    // ANGLE_REACHED(11) sempre traz "Posição final: X° atingida às HH:MM".
    needsReason(status) {
      return [5, 6, 7, 8, PIR_SCHEDULE_SAFETY_FAULT, PIR_SCHEDULE_ANGLE_REACHED].includes(Number(status));
    },
    // No 9 e no 11 o GENERIC é informativo (posição/alvo), não motivo de
    // bloqueio -- não pintar de vermelho.
    isNormalStatus(status) {
      return [9, PIR_SCHEDULE_ANGLE_REACHED].includes(Number(status));
    },
    angleReached(s) {
      return Number(this.liveStatus(s).STATUS) === PIR_SCHEDULE_ANGLE_REACHED;
    },
    positionUnavailable(s) {
      return pirSchedulePosition(this.liveStatus(s)).unavailable;
    },
    // Só faz sentido comparar com o equipamento pros status que dizem
    // respeito à janela vigente agora (rodando ou tentando rodar) -- pra
    // EM ESPERA/PARADA/etc. o estado atual do PIR não tem relação com essa
    // agenda específica.
    isLiveWindowStatus(status) {
      return PIR_SCHEDULE_LIVE_STATUSES.includes(Number(status));
    },
    equipmentSummary() {
      const parts = [
        pivotDirection(this.item),
        pivotWorkMode(this.item),
        pivotStatusText(this.item),
      ].filter(Boolean);
      return parts.length ? parts.join(" · ") : "sem leitura";
    },
    // Sinaliza quando a agenda diz que não está rodando (bloqueada/
    // indisponível) mas o próprio equipamento reporta OPEN -- foi assim que
    // achamos, testando ao vivo, um bug no fw_cir onde a agenda ficava
    // presa em "PIR indisponível" com o pivô girando de verdade.
    liveMismatch(s) {
      const m = this.liveStatus(s);
      if (!m) return false;
      return Number(m.STATUS) !== 9 && !!this.item.OPEN;
    },
    // min/max no <input type="number"> não bloqueia o valor enviado (só o
    // atributo HTML) -- sem checar aqui, o botão "Salvar" ficava habilitado
    // mesmo com um ângulo fora de 0-360 (achado testando: END_ANGLE=440 foi
    // salvo com sucesso). null é válido (campo opcional).
    angleValid(v) {
      return v === null || v === undefined || v === "" || (Number(v) >= 0 && Number(v) <= 360);
    },
    // Mesma fonte usada pelo gatilho de condição do ScheduleForm.vue --
    // sensors.list() já devolve {sensors, aux} prontos pro searchable-select
    // + medição por POS.
    loadSensors() {
      sensors
        .list()
        .then((ret) => {
          this.sensors = ret.sensors || [];
          this.auxSensors = ret.aux || [];
        })
        .catch((err) => console.log(err));
    },
    // Agendamento por ângulo (#441) só faz sentido com posição confiável em
    // tempo real -- hoje só via sensor SPG associado (CAD_VALVE.FK_SENSOR_POS,
    // mesma associação usada pelo mapa de trajetória em PivotSensorChart.vue).
    // Sem isso, os campos de ângulo ficam ocultos em vez de aceitar um valor
    // que o firmware não tem como confirmar.
    checkPositionSensor() {
      valves
        .list()
        .then((ret) => {
          const list = Array.isArray(ret) ? ret : [];
          const row = list.find((v) => v.NAME === this.item.NAME);
          this.hasPositionSensor = !!(row && row.FK_SENSOR_POS);
        })
        .catch((err) => {
          console.log(err);
          this.hasPositionSensor = false;
        });
    },
    isDayActive(activityDays, index) {
      return !!(activityDays & this.days[index].value);
    },
    // ACTIVE vem do banco como TINYINT(1) -- number, não boolean -- e pode
    // vir null em respostas antigas de cache; trata ausência como habilitada.
    isActive(s) {
      return s.ACTIVE === null || s.ACTIVE === undefined ? true : !!Number(s.ACTIVE);
    },
    // Intercepta o clique do switch antes do toggle nativo (preventDefault),
    // pra ele só refletir o novo estado depois da confirmação -- sem isso o
    // checkbox flipa visualmente antes do usuário decidir.
    askToggleActive(s) {
      this.activeTarget = s;
      this.showActiveConfirm = true;
    },
    toMinutesOfDay(t) {
      if (!t) return 0;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + (m || 0);
    },
    minutesLabel(m) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    },
    durationValid(v) {
      return v !== null && v !== undefined && v !== "" && Number(v) > 0 && Number(v) <= 1440;
    },
    // Horário de fim = início + duração, virando o dia se passar da
    // meia-noite -- mesma conta da agenda tradicional (endingPrediction()
    // em Schedules/View.vue).
    scheduleEndLabel(s) {
      const start = this.toMinutesOfDay(s.START_TIME);
      let end = start + Number(s.DURATION_MIN || 0);
      if (end > 1440) end = end % 1440;
      return this.minutesLabel(end);
    },
    // Janelas [START_TIME, START_TIME+DURATION_MIN) se cruzam -- horários de
    // início diferentes ainda conflitam se a duração de uma alcançar o
    // início da outra. DURATION_MIN pode passar de 1440 (virar o dia), então
    // comparamos também com cópias da janela de "b" deslocadas +-1 dia, já
    // que as agendas se repetem diariamente.
    timeWindowsOverlap(a, b) {
      const aStart = this.toMinutesOfDay(a.START_TIME);
      const aEnd = aStart + Number(a.DURATION_MIN || 0);
      const bStart = this.toMinutesOfDay(b.START_TIME);
      const bEnd = bStart + Number(b.DURATION_MIN || 0);
      const overlaps = (s1, e1, s2, e2) => s1 < e2 && s2 < e1;
      return (
        overlaps(aStart, aEnd, bStart, bEnd) ||
        overlaps(aStart, aEnd, bStart - 1440, bEnd - 1440) ||
        overlaps(aStart, aEnd, bStart + 1440, bEnd + 1440)
      );
    },
    dateRangesOverlap(a, b) {
      const aStart = a.START_DATE || "0000-00-00";
      const aEnd = a.END_DATE || "9999-12-31";
      const bStart = b.START_DATE || "0000-00-00";
      const bEnd = b.END_DATE || "9999-12-31";
      return aStart <= bEnd && bStart <= aEnd;
    },
    // WEEKDAYS x WEEKDAYS: compara o bitmask. ODD x EVEN nunca caem no mesmo
    // dia; ODD x ODD ou EVEN x EVEN sempre caem. WEEKDAYS x ODD/EVEN: um dia
    // da semana cai em data par ou ímpar dependendo do mês, então há
    // sobreposição eventual sempre que a WEEKDAYS tiver algum dia marcado.
    daysOverlap(a, b) {
      const am = a.RECURRENCE_MODE || "WEEKDAYS";
      const bm = b.RECURRENCE_MODE || "WEEKDAYS";
      if (am === "WEEKDAYS" && bm === "WEEKDAYS") {
        return !!(Number(a.ACTIVITY_DAYS) & Number(b.ACTIVITY_DAYS));
      }
      if (am !== "WEEKDAYS" && bm !== "WEEKDAYS") {
        return am === bm;
      }
      const weekdays = am === "WEEKDAYS" ? a : b;
      return Number(weekdays.ACTIVITY_DAYS) !== 0;
    },
    schedulesConflict(a, b) {
      return this.timeWindowsOverlap(a, b) && this.daysOverlap(a, b) && this.dateRangesOverlap(a, b);
    },
    hasConflict(s) {
      return !!(this.scheduleConflicts[s.ID] && this.scheduleConflicts[s.ID].length);
    },
    conflictTitle(s) {
      const names = (this.scheduleConflicts[s.ID] || []).map((c) => c.NAME);
      return names.length ? `Sobrepõe com: ${names.join(", ")}` : "";
    },
    confirmToggleActive() {
      if (!this.activeTarget) return;
      const s = this.activeTarget;
      const newActive = !this.isActive(s);
      pirSchedules
        .toggleActive({ ID: s.ID, ACTIVE: newActive })
        .then((ret) => {
          alert(ret.log);
          if (ret.alert === "s") s.ACTIVE = newActive;
        })
        .catch((err) => {
          console.log(err);
          alert("Erro ao atualizar a agenda. Tente novamente.");
        })
        .then(() => {
          this.activeTarget = null;
        });
    },
    toggleDay(index) {
      const bit = this.days[index].value;
      this.form.ACTIVITY_DAYS = this.isDayActive(this.form.ACTIVITY_DAYS, index)
        ? this.form.ACTIVITY_DAYS - bit
        : this.form.ACTIVITY_DAYS + bit;
    },
    fetchList() {
      this.loading = true;
      pirSchedules
        .list()
        .then((ret) => {
          // item.ID chega como STRING do JSON do firmware ("ID":"1000048"),
          // FK_VALVE chega como NUMBER da query (coluna int) -- === falha
          // silenciosamente sem o Number() nos dois lados.
          this.schedules = (ret || []).filter(
            (s) => Number(s.FK_VALVE) === Number(this.item.ID)
          );
        })
        .catch((err) => {
          console.log(err);
          alert("Erro ao carregar as agendas deste pivô.");
        })
        .then(() => {
          this.loading = false;
        });
    },
    openForm(schedule) {
      if (schedule) {
        this.form = {
          ...EMPTY_FORM(),
          ...schedule,
          // O driver mysql do knex devolve TINYINT(1) como number (0/1), não
          // boolean -- b-form-radio-group compara por === com sentidoOptions/
          // modoOptions/canhaoOptions (true/false/null) e não marcava nada.
          REVERSED: triBool(schedule.REVERSED),
          WET_MODE: triBool(schedule.WET_MODE),
          CANNON: triBool(schedule.CANNON),
          PEAK_HOUR_BLOCK: !!Number(schedule.PEAK_HOUR_BLOCK),
          FOLLOW_ANGLE: !!Number(schedule.FOLLOW_ANGLE),
        };
        this.hasEnd = !!(schedule.END_DATE || schedule.END_TIME);
        this.allDayDuration = Number(schedule.DURATION_MIN) === 1440;
        this.conditional = schedule.FK_SENSOR !== null && schedule.FK_SENSOR !== undefined;
      } else {
        this.form = { ...EMPTY_FORM(), FK_VALVE: Number(this.item.ID) };
        this.hasEnd = false;
        this.allDayDuration = false;
        this.conditional = false;
      }
      this.showForm = true;
    },
    save() {
      if (!this.valid) return;
      const data = { ...this.form };
      if (!this.hasEnd) {
        data.END_DATE = null;
        data.END_TIME = null;
      }
      if (!this.conditional) {
        data.FK_SENSOR = null;
        data.POS_SENSOR = null;
        data.MIN_SENSOR = null;
        data.MAX_SENSOR = null;
        data.ACTION_SENSOR = null;
      }
      const action = data.ID ? pirSchedules.edit(data) : pirSchedules.add(data);
      action
        .then((ret) => {
          alert(ret.log);
          if (ret.alert === "s") this.fetchList();
        })
        .catch((err) => {
          console.log(err);
          alert("Erro ao salvar a agenda. Tente novamente.");
        });
    },
    remove() {
      if (!this.form.ID) return;
      pirSchedules
        .del([{ ID: this.form.ID }])
        .then((ret) => {
          alert(ret.log);
          if (ret.alert !== "e") this.fetchList();
        })
        .catch((err) => {
          console.log(err);
          alert("Erro ao remover a agenda. Tente novamente.");
        });
    },
  },
};
</script>

<style scoped>
.pivot-timeline {
  margin-bottom: 12px;
}

.pivot-timeline-bar {
  display: flex;
  width: 100%;
  height: 20px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.pivot-timeline-seg {
  height: 100%;
  border-right: 1px solid rgba(255, 255, 255, 0.4);
}

.pivot-timeline-seg:last-child {
  border-right: none;
}

.seg-wet {
  background-color: #298abe;
}

.seg-dry {
  background-color: #eb753e;
}

.seg-neutral {
  background-color: #9e9e9e;
}

.seg-idle {
  background-color: #f0f0f0;
}

.pivot-timeline-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.65em;
  color: #999;
  margin-top: 2px;
}

.pivot-schedule-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pivot-schedule-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
}

.pivot-schedule-card:hover {
  background-color: #f7f7f7;
}

.pivot-schedule-card.inactive {
  opacity: 0.55;
}

.pivot-schedule-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85em;
}

.pivot-schedule-head-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pivot-schedule-window {
  color: #666;
  font-size: 0.9em;
}

.pivot-schedule-active-toggle {
  margin: 0;
}

.pivot-schedule-days {
  display: flex;
  gap: 3px;
  margin-top: 4px;
}

.day-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62em;
  background-color: #eee;
  color: #999;
}

.day-dot.active {
  background-color: #3d9161;
  color: #fff;
}

.pivot-schedule-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.tag {
  font-size: 0.65em;
  background-color: #eef3f1;
  color: #245c3a;
  border-radius: 10px;
  padding: 2px 7px;
}

.tag-warning {
  background-color: #fbeee0;
  color: #a85d00;
}

.tag-danger {
  background-color: #fbe0e0;
  color: #a80000;
}

.schedule-status-tag {
  font-size: 0.55em;
  vertical-align: middle;
  margin-left: 6px;
}

.pivot-schedule-live {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e0e0e0;
}

.live-status-badge {
  font-size: 0.65em;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: #eee;
  color: #777;
}

.live-status-9 {
  background-color: #e1f0e6;
  color: #1f7a3f;
}

.live-status-5,
.live-status-6,
.live-status-7 {
  background-color: #fbe0e0;
  color: #a80000;
}

.live-status-8 {
  background-color: #fbeee0;
  color: #a85d00;
}

.live-status-11 {
  background-color: #e0ecf8;
  color: #1f5a8a;
}

.live-status-10,
.live-status-fault {
  background-color: #a80000;
  color: #fff;
}

.live-fault {
  color: #a80000;
  font-weight: bold;
}

.live-status-manual {
  background-color: #fbeee0;
  color: #a85d00;
}

.live-manual {
  color: #a85d00;
}

.live-progress {
  position: relative;
  height: 14px;
  margin-top: 4px;
  border-radius: 4px;
  background-color: #eee;
  overflow: hidden;
}

.live-progress-bar {
  height: 100%;
  background-color: #3d9161;
}

.live-progress-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.6em;
  line-height: 14px;
  color: #333;
}

.live-reason {
  margin-top: 4px;
  font-size: 0.65em;
  color: #a80000;
}

.live-reason-info {
  color: #555;
}

.live-position-unavailable {
  color: #a85d00;
  font-weight: bold;
}

.live-reason-missing {
  color: #999;
  font-style: italic;
}

.live-equipment {
  margin-top: 4px;
  font-size: 0.65em;
  color: #666;
}

.live-mismatch {
  color: #a85d00;
  font-weight: bold;
}

.obs-info {
  text-align: center;
  font-size: 0.68em;
}

.current-value {
  color: #aaa;
  font-weight: normal;
  font-style: italic;
}
</style>
